/**
 * Slideshow for sections/nasty-g-hero.liquid.
 *
 * The Zap slideshow it is ported from runs Swiper with `speed: 0`, so the layer swap is
 * instantaneous and every bit of perceived motion is the incoming slide's content re-running its
 * entrance keyframes. That is the whole trick, and it means there is no track to translate and no
 * momentum to model: the slides are stacked in one grid cell, one is visible, and this file only has
 * to pick which one and restart its keyframes.
 *
 * Three departures from the donor, each named in the port spec as a defect rather than a target.
 *
 * 1. Zap sets `allowTouchMove: false`, so its full screen mobile hero cannot be swiped. Swipe is
 *    restored here from pointer events; there is no track, so a threshold on the pointer delta is
 *    the whole implementation.
 * 2. Zap sets `mousewheel: { forceToAxis: true }`, which turns a vertical wheel gesture over the
 *    first screen of the page into a slide change and makes the page feel stuck. Not ported.
 * 3. Zap has no autoplay at all. Ours advances, because our slides carry a claim and a link rather
 *    than a two word flavour name, and a claim nobody sees is not a claim. It stops for reduced
 *    motion, for a hidden tab, and for a reader who is hovering or tabbing through the fold.
 */

const reduced = matchMedia('(prefers-reduced-motion: reduce)');

const FALLBACK_MS = 6000;

/* Horizontal travel that counts as a swipe rather than a tap or the start of a page scroll. */
const SWIPE_PX = 40;

class NastyHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));

    /*
     * Before anything to do with the carousel, and before the single slide return below, because a
     * static hero has a headline and a footer that have to clear each other exactly as much as an
     * animated one does.
     */
    this.watchFoot();

    /*
     * One slide is a static hero. Paint it ready so the arrows stay unrendered and leave: with no
     * script at all the same thing happens, and the first slide is a complete, linked fold either
     * way.
     */
    if (this.slides.length < 2) {
      this.toggleAttribute('data-hero-ready', true);
      return;
    }

    this.index = 0;
    this.interval = Number(this.dataset.autoplayMs) || FALLBACK_MS;
    this.elapsed = 0;
    this.startedAt = 0;
    this.hovered = false;
    this.focused = false;
    this.swiped = false;
    this.paused = false;

    for (const button of this.querySelectorAll('[data-hero-step]')) {
      button.addEventListener('click', () => this.step(Number(button.dataset.heroStep)));
    }

    /*
     * The host is not focusable, so this only fires when focus is already on an arrow or on the
     * live slide's link. Arrow keys elsewhere on the page keep scrolling it.
     */
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      else if (event.key === 'ArrowRight') this.step(1);
      else return;
      event.preventDefault();
    });

    this.addEventListener('pointerdown', (event) => {
      this.origin = { x: event.clientX, y: event.clientY };
      this.swiped = false;
    });

    this.addEventListener('pointerup', (event) => {
      if (!this.origin) return;
      const dx = event.clientX - this.origin.x;
      const dy = event.clientY - this.origin.y;
      this.origin = null;

      /* Dominantly horizontal, or it was the beginning of a page scroll and belongs to the page. */
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
      this.swiped = true;
      this.step(dx < 0 ? 1 : -1);
    });

    /*
     * A swipe that starts and ends on the slide's link still fires a click, so the link would open
     * on every swipe. One capturing listener swallows exactly that click.
     */
    this.addEventListener(
      'click',
      (event) => {
        if (!this.swiped) return;
        this.swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    /*
     * Hover and focus are held separately: a reader who tabs to an arrow and then moves the mouse
     * away has not stopped reading.
     */
    this.addEventListener('pointerenter', () => {
      this.hovered = true;
      this.applyPause();
    });
    this.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.applyPause();
    });
    this.addEventListener('focusin', () => {
      this.focused = true;
      this.applyPause();
    });
    this.addEventListener('focusout', () => {
      this.focused = false;
      this.applyPause();
    });

    this.onVisibility = () => this.applyPause();
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onMotionChange = () => {
      if (reduced.matches) clearTimeout(this.timer);
      else this.restart();
    };
    reduced.addEventListener('change', this.onMotionChange);

    this.toggleAttribute('data-hero-ready', true);
    this.restart();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    reduced.removeEventListener('change', this.onMotionChange);
    this.footObserver?.disconnect();
  }

  /**
   * Publishes the real height of the footer into `--nz-foot-space`.
   *
   * From 990 up the footer is absolutely pinned to the bottom of the slide, so the space it occupies
   * has to be reserved or the headline centres against the whole slide and lands on top of it. The
   * section used to reserve a constant, measured by hand once at one viewport against one set of
   * type. That number is a promise about an element nobody has to keep: enlarge the supporting line,
   * enlarge the button, write a longer line in the theme editor, or let a reader raise their default
   * font size, and the constant silently under-reserves. The failure is not a wrapped line, it is
   * 320px of headline printed through the copy.
   *
   * Measuring removes the whole class of bug, and it is the pattern the theme already uses for the
   * masthead in `snippets/measure-header-heights.liquid`. `--nz-step` then reads these two variables
   * (and `--nz-lede`, immediately below) rather than the arithmetic of their old values, so the
   * headline resizes itself to whatever room is actually left.
   *
   * `--nz-foot-space` is the button's own measured height and nothing more: the gap in front of it
   * is `--nz-cta-gap`, a plain design value declared beside `--nz-lede-gap` in the stylesheet, not
   * folded in here. The two used to be one number, back when the button was pinned to the section's
   * own edge and the space above it was a single reservation; now that the button is an ordinary
   * flow child positioned by its own margin, there is no reason left to keep them married.
   *
   * Two details worth keeping:
   *
   *  - It measures the tallest footer and the tallest supporting line, not the current slide's. The
   *    line is reserved separately, as `--nz-lede`, because it is in flow rather than pinned: it
   *    costs the headline height without being part of the footer's box. The slides are stacked in
   *    one grid cell
   *    and the inactive ones are `visibility: hidden`, which still lays them out, so all of them can
   *    be measured at once. Reserving for the tallest is what stops the headline changing size as
   *    the carousel cycles -- the same reason `--nz-lines` exists a few lines further up in the
   *    stylesheet.
   *  - There is no feedback loop to guard against. The headline's size depends on this measurement,
   *    but the footer's height depends only on its own copy and the viewport width, never on the
   *    headline, so observing one cannot be triggered by writing the other.
   */
  watchFoot() {
    const foots = Array.from(this.querySelectorAll('.nasty-g-hero__foot'));
    const lines = Array.from(this.querySelectorAll('.nasty-g-hero__line'));
    if (!foots.length && !lines.length) return;

    /* Tallest, never current: a one line slide and a two line slide must not size their headlines
       differently, or the type changes size as the carousel cycles. */
    const tallest = (elements, height) =>
      elements.reduce((max, el) => Math.max(max, height(el)), 0);

    const boxHeight = (el) => el.getBoundingClientRect().height;

    /*
     * The natural height of the tallest line, measured with the floor it is about to set lifted.
     *
     * The stylesheet gives every line `min-height: var(--nz-lede)` so the composition above them
     * does not shift as the carousel cycles. Measuring those boxes directly would measure the value
     * being computed: once a two line slide had set the floor, a viewport wide enough to pull every
     * line back onto one line would still measure the old height, and the reservation could rise but
     * never fall.
     *
     * Zeroing the variable first is the whole trick -- the boxes collapse to their content, the read
     * below forces the layout that makes that true, and the previous value goes back immediately, so
     * nothing is ever painted in the collapsed state.
     *
     * A Range over the contents was tried first and is wrong: it reports the inline boxes and not
     * the line boxes, so it misses the leading. Measured on the live page, a two line 18px/1.5 line
     * came back as 49px against a real 54px, and the slide overran the fold by exactly that 5px.
     */
    const naturalLede = () => {
      const previous = this.style.getPropertyValue('--nz-lede');
      this.style.setProperty('--nz-lede', '0px');
      const height = tallest(lines, boxHeight);
      if (previous) this.style.setProperty('--nz-lede', previous);
      else this.style.removeProperty('--nz-lede');
      return height;
    };

    const publish = (prop, value) => {
      /* A hidden section measures zero. Leave the stylesheet's fallback in place rather than
         reserving nothing and letting the headline print through the copy. */
      if (!value) return;
      if (this[prop] === value) return;
      this[prop] = value;
      this.style.setProperty(prop, `${value}px`);
    };

    const measure = () => {
      /* `naturalLede` writes to this element to take its reading, which the observer below sees.
         Without this the callback re-enters itself once per measurement. */
      if (this.measuring) return;
      this.measuring = true;

      const foot = tallest(foots, boxHeight);
      /* Guarded rather than folded into `publish`: a slide with no button at all measures zero, and
         writing that would reserve room for a button that is not there. */
      if (foot) publish('--nz-foot-space', Math.ceil(foot));
      publish('--nz-lede', Math.ceil(naturalLede()));

      this.measuring = false;
    };

    measure();

    /* Text metrics decide all of this, so re-measure once the real faces are in. */
    document.fonts?.ready.then(measure);

    if (typeof ResizeObserver !== 'function') return;
    this.footObserver = new ResizeObserver(measure);
    /*
     * The host is observed as well as the parts. A line pinned to `min-height` stops changing size
     * when the viewport rewraps it, so observing the lines alone would miss exactly the resize that
     * needs a new measurement; the host's width always changes.
     */
    for (const el of [this, ...foots, ...lines]) this.footObserver.observe(el);
  }

  /** Wraps in both directions, which is the donor's `loop: true`, so no arrow is ever dead. */
  step(delta) {
    const count = this.slides.length;
    this.show((this.index + delta + count) % count);
  }

  show(next) {
    if (next === this.index) return;
    this.index = next;
    this.paint();
    this.restart();
  }

  paint() {
    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.toggleAttribute('data-current', current);

      /*
       * `visibility: hidden` in the sheet is what actually keeps the resting slides out of the tab
       * order, in every browser. `inert` and `aria-hidden` state the same thing for assistive tech
       * that walks the tree rather than the layout.
       */
      slide.toggleAttribute('inert', !current);
      if (current) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    const live = this.slides[this.index];
    live.removeAttribute('data-animate');
    /* Read a layout property to flush the removal, or the re-add coalesces and nothing restarts. */
    void live.offsetWidth;
    live.setAttribute('data-animate', '');
  }

  applyPause() {
    const paused = this.hovered || this.focused || document.hidden;
    if (paused === this.paused) return;
    this.paused = paused;

    if (paused) {
      if (this.startedAt) this.elapsed += performance.now() - this.startedAt;
      this.startedAt = 0;
      clearTimeout(this.timer);
      return;
    }

    /*
     * Resume on what is left, not on a fresh interval: a slide that was already five seconds old
     * when the pointer arrived should not owe another six.
     */
    this.run(Math.max(0, this.interval - this.elapsed));
  }

  restart() {
    clearTimeout(this.timer);
    this.elapsed = 0;
    if (!this.paused) this.run(this.interval);
  }

  run(ms) {
    if (reduced.matches) return;
    this.startedAt = performance.now();
    this.timer = setTimeout(() => this.step(1), ms);
  }
}

if (!customElements.get('nasty-hero')) {
  customElements.define('nasty-hero', NastyHero);
}
